// Ubicación en el repo: src/lib/hearingaids.js
// Audífonos — venta y calibración (tabla `hearingaids`). Fase 1 (ver
// DECISIONS.md): la venta en sí y el descuento de stock del catálogo. Fase 2
// agrega los controles/calibraciones (tabla `hearingaid_controls`, ya usada
// por hearingaidAlerts.js) — acá solo se embeben para mostrar el estado
// (próximo/vencido/sin pendientes) en el listado; el CRUD de cada control
// vive en hearingaidControls.js.
//
// A diferencia de exams/prescriptions, `hearingaids` tiene su propia
// columna `branch_id` (no depende de patient_branches para filtrar), así
// que el filtro por sucursal activa va directo en la consulta — RLS
// (`has_branch_access(branch_id)`) igual es la protección real.
import { supabase } from './supabaseClient';

// `catalog(image_path, warranty_terms)` (migración 0052, 16-sep-2026): a
// diferencia de brand/model/price (snapshot de la venta, ver comentario más
// abajo), la foto y las condiciones de garantía SÍ se leen del producto tal
// como está HOY en el catálogo, no como estaban el día de la venta — si se
// corrige una condición de garantía después, el documento impreso de una
// venta antigua de ese mismo modelo debe reflejar el texto vigente. Sin
// `!fk_column` porque `catalog_id` es la única FK de hearingaids hacia
// catalog (ver migración 0001).
// `professionals!created_by(...)` (mismo criterio que hearingaid_quotes.js):
// hearingaids tiene 3 FKs hacia professionals (created_by/updated_by/
// deleted_by), así que hace falta desambiguar con `!created_by` — quien
// registró la venta es el dato relevante para mostrar "Vendido/atendido
// por" en el documento de garantía. `signature_path`/`rut` (16-sep-2026,
// pedido de Nicolás) — la garantía se firma con la firma digital cargada en
// el perfil de ESE profesional (mismo campo que ya usan
// examReport.jsx/prescriptionReport.jsx), no una firma genérica de la
// clínica.
const HEARINGAID_SELECT =
  'id, patient_id, branch_id, catalog_id, brand, model, side, units_used, serial, price, ' +
  'serial_od, serial_oi, price_od, price_oi, purchase_date, warranty_months, warranty_end, notes, ' +
  'patients(nombre, apellido, segundo_nombre, segundo_apellido, rut), ' +
  'hearingaid_controls(id, date, done, status, deleted_at), ' +
  'catalog(image_path, warranty_terms), ' +
  'professionals!created_by(nombre, apellido, role, rut, signature_path)';

export async function listHearingaids({ branchId, search } = {}) {
  if (!branchId) return [];
  let query = supabase
    .from('hearingaids')
    .select(HEARINGAID_SELECT)
    .is('deleted_at', null)
    .eq('branch_id', branchId)
    .order('purchase_date', { ascending: false });
  const { data, error } = await query;
  if (error) throw error;

  let rows = (data ?? []).map((h) => ({
    ...h,
    hearingaid_controls: (h.hearingaid_controls || []).filter((c) => !c.deleted_at),
  }));
  const term = search?.trim().toLowerCase();
  if (term) {
    rows = rows.filter((h) => {
      const p = h.patients;
      if (!p) return false;
      const name = [p.nombre, p.segundo_nombre, p.apellido, p.segundo_apellido].filter(Boolean).join(' ').toLowerCase();
      return name.includes(term) || (p.rut || '').toLowerCase().includes(term);
    });
  }
  return rows;
}

export async function getHearingaidById(id) {
  const { data, error } = await supabase.from('hearingaids').select('*').eq('id', id).single();
  if (error) throw error;
  return data;
}

// El ajuste de stock (descontar al crear, restituir+descontar al editar si
// cambia modelo/lado/sucursal, restituir al eliminar) lo hace el trigger
// trg_hearingaids_stock_sync en la base de datos (migración 0002, desde la
// Fase B —migración 0049— usa fn_adjust_branch_stock, por sucursal) — no se
// duplica acá. Antes este archivo también llamaba a adjustCatalogStock() en
// cada caso, lo que descontaba el stock dos veces por venta (ver
// DECISIONS.md).
export async function createHearingaid(values, actorId) {
  const { error } = await supabase.from('hearingaids').insert({
    ...values,
    created_by: actorId,
    updated_by: actorId,
  });
  if (error) throw error;
}

// `previous` (código muerto, hallazgo #4 de la auditoría técnica del
// 11-sep-2026, menor/no urgente) se recibía y nunca se usaba — quitado del
// nombre y del único call site (HearingAidFormModal.jsx).
export async function updateHearingaid(id, values, actorId) {
  const { error } = await supabase
    .from('hearingaids')
    .update({ ...values, updated_by: actorId })
    .eq('id', id);
  if (error) throw error;
}

export async function softDeleteHearingaid(hearingaid, actorId) {
  const { error } = await supabase
    .from('hearingaids')
    .update({ deleted_at: new Date().toISOString(), deleted_by: actorId })
    .eq('id', hearingaid.id);
  if (error) throw error;
}

// Mismo resumen que hearingAidSummaryLine() del prototipo, usado en el
// listado y en la ficha de paciente.
export function hearingAidSummaryLine(h) {
  const parts = [h.brand, h.model, `· ${h.side}`];
  return parts.filter(Boolean).join(' ');
}
